import React from 'react'
import { Card, CardHeader, CardContent, Label, Slider, Button, Switch, Textarea } from '@/shared/ui'
import { useAuth } from '@/modules/auth/context/AuthContext'
import { isMasterByEmail } from '@/shared/utils/role'
import { useToast } from '@/shared/ui/Toast'
import { getThermometerWeights, setThermometerWeights, resetThermometerWeights, DEFAULT_THERMOMETER_WEIGHTS, type ThermometerWeights } from '@/modules/termometro/services/thermometerWeightsService'
import { getThermometerMemberTip, setThermometerMemberTip, DEFAULT_THERMOMETER_MEMBER_TIP } from '@/modules/termometro/services/thermometerTextService'

type Key = keyof ThermometerWeights['weights']

const FIELDS: { key: Key; label: string; hint?: string }[] = [
  { key: 'rsi_favorable', label: 'RSI favoravel (TF menor da oportunidade)', hint: 'Quao perto o RSI esta da ancora (30 Long / 70 Short).' },
  { key: 'rsi_alignment_pos', label: 'RSI alinhado ao preco (positivo)', hint: 'Preco e RSI se movem a favor do objetivo.' },
  { key: 'rsi_alignment_neg', label: 'RSI divergente do preco (negativo)', hint: 'Preco e RSI se movem contra o objetivo.' },
  { key: 'ema_context', label: 'EMAs coerentes com o contexto', hint: 'Preco posicionado em relacao as EMAs 12/26/200 no menor e maior TF.' },
  { key: 'ema_react_12_pos', label: 'Reacao favoravel em EMA 12 (menor TF)', hint: 'Toque na EMA 12 seguido de reacao a favor.' },
  { key: 'ema_react_12_neg', label: 'Reacao desfavoravel em EMA 12 (menor TF)', hint: 'Toque na EMA 12 seguido de reacao contra.' },
  { key: 'ema_react_26_pos', label: 'Reacao favoravel em EMA 26 (menor TF)', hint: 'Toque na EMA 26 seguido de reacao a favor.' },
  { key: 'ema_react_26_neg', label: 'Reacao desfavoravel em EMA 26 (menor TF)', hint: 'Toque na EMA 26 seguido de reacao contra.' },
  { key: 'fibo_zone_pos', label: 'Preco na zona de Fibo (0.382/0.5/0.618)' },
  { key: 'fibo_far_neg', label: 'Preco longe das zonas de Fibo (penalidade)' },
  { key: 'volume_correction', label: 'Volume decrescente na correcao (TF maior)' },
  { key: 'pivot_minor_pos', label: 'Pivo do TF sequencial a favor' },
  { key: 'pivot_minor_neg', label: 'Pivo do TF sequencial contra' },
  { key: 'breakout_penalty', label: 'Penalidade por rompimento recente (dureza)', hint: 'Controla a dureza quando rompe extremos recentes do TF maior.' },
]

export default function ThermometerManage() {
  const { user } = useAuth()
  const master = isMasterByEmail(user?.email)
  const { add: toast } = useToast()
  const [loading, setLoading] = React.useState(true)
  const [saving, setSaving] = React.useState(false)
  const [state, setState] = React.useState<ThermometerWeights>(DEFAULT_THERMOMETER_WEIGHTS)
  const [tip, setTip] = React.useState<string>(DEFAULT_THERMOMETER_MEMBER_TIP)

  React.useEffect(() => {
    let mounted = true
    getThermometerWeights()
      .then((cfg) => { if (mounted) setState(cfg) })
      .catch((e) => toast({ title: 'Termometro', description: e?.message || 'Falha ao carregar', variant: 'error' }))
      .finally(() => { if (mounted) setLoading(false) })
    return () => { mounted = false }
  }, [])

  React.useEffect(() => {
    let mounted = true
    getThermometerMemberTip()
      .then((txt) => { if (mounted) setTip(txt) })
      .catch(() => {})
    return () => { mounted = false }
  }, [])

  function setVal(key: Key, v: number) {
    setState((s) => ({ ...s, weights: { ...s.weights, [key]: Math.max(0, Math.min(10, Math.round(v))) } }))
  }

  async function handleSave() {
    if (!master) return
    setSaving(true)
    try {
      await Promise.all([
        setThermometerWeights(state),
        setThermometerMemberTip(tip),
      ])
      toast({ title: 'Termometro', description: 'Configuracoes salvas com sucesso', variant: 'success', soft: true })
    } catch (e: any) {
      toast({ title: 'Termometro', description: e?.message || 'Falha ao salvar', variant: 'error' })
    } finally {
      setSaving(false)
    }
  }

  async function handleReset() {
    if (!master) return
    setSaving(true)
    try {
      await resetThermometerWeights()
      const cfg = await getThermometerWeights()
      setState(cfg)
      await setThermometerMemberTip(DEFAULT_THERMOMETER_MEMBER_TIP)
      setTip(DEFAULT_THERMOMETER_MEMBER_TIP)
      toast({ title: 'Termometro', description: 'Configuracoes restauradas ao padrao', variant: 'success', soft: true })
    } catch (e: any) {
      toast({ title: 'Termometro', description: e?.message || 'Falha ao restaurar', variant: 'error' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <CardHeader className="text-base font-semibold">Gerenciar Termometro (Master)</CardHeader>
      <CardContent className="space-y-4">
        {!master && (
          <div className="rounded-md border border-border/50 bg-muted/30 p-3 text-sm">Somente o usuario master pode editar estes valores.</div>
        )}

        <div className="flex items-center justify-between gap-4">
          <div className="text-sm text-foreground/80">Habilitar pesos dinamicos do Termometro</div>
          <div className="flex items-center gap-2">
            <Label>Desligado</Label>
            <Switch checked={!!state.enabled} onChange={(e:any)=> setState((s)=> ({...s, enabled: !!(e?.target?.checked)}))} disabled={!master || loading || saving} />
            <Label>Ligado</Label>
          </div>
        </div>

        <div className="space-y-2">
          <div className="text-sm font-medium">Texto explicativo para membros</div>
          <Textarea rows={5} value={tip} onChange={(e:any)=> setTip(String(e?.target?.value || ''))} disabled={!master || loading || saving} placeholder="Texto exibido no icone de ajuda do Termometro" />
          <div className="text-[11px] text-foreground/60">Aparece no icone de ajuda ao lado do titulo “Confluencias do Termometro”.</div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {FIELDS.map(({ key, label, hint }) => (
            <div key={key} className="rounded-md border border-[#1c1c1c] bg-muted/10 p-3">
              <div className="mb-2 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">{label}</div>
                  {hint ? <div className="text-xs text-foreground/70">{hint}</div> : null}
                </div>
                <div className="shrink-0 w-8 text-right text-xs text-foreground/70">{state.weights[key]}</div>
              </div>
              <Slider min={0} max={10} step={1} value={state.weights[key]} onChange={(e:any)=> setVal(key, Number(e?.target?.value || 0))} disabled={!master || loading || saving} />
              <div className="mt-1 flex justify-between text-[11px] text-foreground/60">
                <span>0</span>
                <span>10</span>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={handleReset} disabled={!master || loading || saving}>Restaurar padrao</Button>
          <Button onClick={handleSave} disabled={!master || loading || saving}>Salvar</Button>
        </div>
      </CardContent>
    </Card>
  )
}

